import type { MenuSettingInput } from '@lyrmo/shared';
import { useId, useState, type ReactNode } from 'react';
import { Button } from '../../components/Button';
import { Icon } from '../../components/Icon';
import { useI18n } from '../../i18n/i18n-context';
import { cx } from '../../lib/cx';
import { matches, type MenuItem } from '../rewrite/menu-items';
import type { Trial } from './useTrials';
import './item-manager.css';

/*
 * What Manage AI tools, Manage tones, and Manage instructions share: every choice listed on the
 * left, built-in and the writer's own, and the one chosen on the right, with whether its menu
 * shows it, whether it's pinned, what it does, and a try of it on a sample.
 */

interface ManagerLayoutProps {
  /** How many of the choices the menu shows, of how many there are. */
  readonly shown: number;
  readonly total: number;
  readonly list: ReactNode;
  readonly details: ReactNode;
}

/** Every choice on the left, the one chosen on the right, each scrolling on its own. */
export function ManagerLayout({ shown, total, list, details }: ManagerLayoutProps) {
  const { t } = useI18n();
  return (
    <div className="manager">
      <p className="dialog-intro">{t.menus.inMenu(shown, total)}</p>
      <div className="manager-panes">
        <div className="manager-list">{list}</div>
        <div className="manager-details">{details}</div>
      </div>
    </div>
  );
}

export interface ManagerGroup {
  readonly label: string;
  readonly items: readonly MenuItem[];
  /** Beside the label, such as how many the writer has made. */
  readonly aside?: ReactNode;
  /** In place of the items when there are none, unless searching, such as while they load. */
  readonly empty?: ReactNode;
}

interface ManagerListProps {
  readonly groups: readonly ManagerGroup[];
  /** The choice shown on the right, if any. */
  readonly selected: string | null;
  readonly onSelect: (id: string) => void;
  readonly newLabel: string;
  readonly onNew: () => void;
  readonly newDisabled: boolean;
  /** Under New, such as why it's disabled. */
  readonly footnote?: ReactNode;
}

/** The choices, found by name or what they do, in their groups, then the button to make one. */
export function ManagerList({
  groups,
  selected,
  onSelect,
  newLabel,
  onNew,
  newDisabled,
  footnote,
}: ManagerListProps) {
  const { t } = useI18n();
  const [query, setQuery] = useState('');
  const searching = query.trim() !== '';
  const found = groups.map((group) => ({
    ...group,
    items: group.items.filter((item) => matches(item, query)),
  }));

  return (
    <>
      <label className="manager-search">
        <Icon name="search" />
        <input
          type="search"
          placeholder={t.menus.search}
          aria-label={t.menus.search}
          value={query}
          onChange={(event) => {
            setQuery(event.target.value);
          }}
        />
      </label>

      {found.map(
        ({ label, items, aside, empty }) =>
          (items.length > 0 || (!searching && empty)) && (
            <div key={label} className="manager-group">
              <div className="manager-group-head">
                <h3 className="manager-group-title">{label}</h3>
                {aside && <span className="manager-group-count">{aside}</span>}
              </div>
              {items.length > 0 ? (
                <ul className="manager-items" aria-label={label}>
                  {items.map((item) => (
                    <li key={item.id}>
                      <button
                        type="button"
                        className={cx('manager-item', item.hidden && 'is-hidden')}
                        aria-current={selected === item.id ? 'true' : undefined}
                        onClick={() => {
                          onSelect(item.id);
                        }}
                      >
                        <ItemIcon item={item} />
                        <span className="manager-item-name">{item.name}</span>
                        {item.pinned && (
                          <span
                            className="manager-item-mark is-pinned"
                            role="img"
                            aria-label={t.menus.pinned}
                            title={t.menus.pinned}
                          >
                            <Icon name="pin" />
                          </span>
                        )}
                        {item.hidden && (
                          <span
                            className="manager-item-mark"
                            role="img"
                            aria-label={t.menus.hidden}
                            title={t.menus.hidden}
                          >
                            <Icon name="hidden" />
                          </span>
                        )}
                      </button>
                    </li>
                  ))}
                </ul>
              ) : (
                empty
              )}
            </div>
          ),
      )}

      {searching && found.every((group) => group.items.length === 0) && (
        <p className="manager-empty" role="status">
          {t.menus.noMatch(query.trim())}
        </p>
      )}

      <button
        type="button"
        className="button button--ghost manager-new"
        disabled={newDisabled}
        onClick={onNew}
      >
        <Icon name="plus" />
        {newLabel}
      </button>
      {footnote}
    </>
  );
}

/** A choice's icon: grey for a built-in one, the accent for the writer's own. */
export function ItemIcon({
  item,
  large = false,
}: {
  readonly item: Pick<MenuItem, 'icon' | 'builtIn'>;
  readonly large?: boolean;
}) {
  return (
    <span className={cx('manager-icon', large && 'manager-icon--large', !item.builtIn && 'is-own')}>
      <Icon name={item.icon} />
    </span>
  );
}

/** The chosen choice's name, whether it's built-in or the writer's own, and what to do with it. */
export function ItemHead({
  item,
  actions,
}: {
  readonly item: MenuItem;
  readonly actions: ReactNode;
}) {
  const { t } = useI18n();
  return (
    // Not a <header>: inside a dialog or an app's page, that would be another banner landmark.
    <div className="item-head">
      <ItemIcon item={item} large />
      <div className="item-title">
        <h3 className="item-name">{item.name}</h3>
        <p className={cx('item-kind', !item.builtIn && 'is-own')}>
          {item.builtIn && <Icon name="lock" />}
          {item.builtIn ? t.menus.builtInBadge : t.menus.ownBadge}
        </p>
      </div>
      <div className="item-actions">{actions}</div>
    </div>
  );
}

interface OwnActionsProps {
  readonly name: string;
  readonly onEdit: () => void;
  readonly onDuplicate: () => void;
  readonly onDelete: () => void;
  /** As many of their own as the writer can have. */
  readonly full: boolean;
  readonly busy: boolean;
}

/** What the writer can do with one of their own: change it, duplicate it, or delete it. */
export function OwnActions({ name, onEdit, onDuplicate, onDelete, full, busy }: OwnActionsProps) {
  const { t } = useI18n();
  return (
    <>
      <button
        type="button"
        className="button button--ghost item-button"
        aria-label={t.menus.editItem(name)}
        onClick={onEdit}
      >
        <Icon name="edit" />
        {t.menus.edit}
      </button>
      <button
        type="button"
        className="button button--ghost icon-button"
        aria-label={t.menus.duplicateItem(name)}
        title={t.menus.duplicateItem(name)}
        disabled={full}
        onClick={onDuplicate}
      >
        <Icon name="copy" />
      </button>
      <button
        type="button"
        className="button button--ghost icon-button"
        aria-label={t.menus.deleteItem(name)}
        title={t.menus.deleteItem(name)}
        disabled={busy}
        onClick={onDelete}
      >
        <Icon name="trash" />
      </button>
    </>
  );
}

/** Built-in choices can't be changed: duplicated, they start one of the writer's own. */
export function DuplicateBuiltIn({
  disabled,
  onClick,
}: {
  readonly disabled: boolean;
  readonly onClick: () => void;
}) {
  const { t } = useI18n();
  return (
    <button
      type="button"
      className="button button--ghost item-button"
      disabled={disabled}
      onClick={onClick}
    >
      <Icon name="copy" />
      {t.menus.duplicateBuiltIn}
    </button>
  );
}

/** Asks before deleting one of the writer's own. */
export function ConfirmDelete({
  message,
  busy,
  onCancel,
  onConfirm,
}: {
  readonly message: string;
  readonly busy: boolean;
  readonly onCancel: () => void;
  readonly onConfirm: () => void;
}) {
  const { t } = useI18n();
  return (
    <div className="item-confirm" role="group" aria-label={t.menus.delete}>
      <p>{message}</p>
      <div className="item-confirm-actions">
        <Button onClick={onCancel}>{t.common.cancel}</Button>
        <Button variant="primary" disabled={busy} onClick={onConfirm}>
          {t.menus.delete}
        </Button>
      </div>
    </div>
  );
}

/** One way of keeping a choice: what it is and does, then its switch. */
function Setting({
  label,
  hint,
  on,
  disabled,
  onChange,
}: {
  readonly label: string;
  readonly hint: string;
  readonly on: boolean;
  readonly disabled: boolean;
  readonly onChange: (on: boolean) => void;
}) {
  const id = useId();
  return (
    <div className="item-setting">
      <span className="item-setting-text">
        <span className="item-setting-label" id={`${id}-label`}>
          {label}
        </span>
        <span className="item-setting-hint" id={`${id}-hint`}>
          {hint}
        </span>
      </span>
      <input
        type="checkbox"
        role="switch"
        className="switch"
        checked={on}
        disabled={disabled}
        aria-labelledby={`${id}-label`}
        aria-describedby={`${id}-hint`}
        onChange={(event) => {
          onChange(event.target.checked);
        }}
      />
    </div>
  );
}

interface ItemSettingsProps {
  readonly item: MenuItem;
  /** Whether the writer's settings have loaded, so the switches say how it's kept. */
  readonly ready: boolean;
  readonly onKeep: (change: Partial<MenuSettingInput>) => void;
  /** Why the choice can't be hidden, when it can't, such as the default tone. */
  readonly alwaysShown?: string | undefined;
}

/** Whether the menu shows the choice, and whether it's pinned to its top. */
export function ItemSettings({ item, ready, onKeep, alwaysShown }: ItemSettingsProps) {
  const { t } = useI18n();
  return (
    <div className="item-settings">
      <Setting
        label={t.menus.showInMenu}
        hint={alwaysShown ?? t.menus.showInMenuHint}
        on={!item.hidden}
        disabled={!ready || alwaysShown !== undefined}
        onChange={(on) => {
          onKeep({ hidden: !on });
        }}
      />
      <Setting
        label={t.menus.pin}
        hint={t.menus.pinHint}
        on={item.pinned}
        disabled={!ready}
        onChange={(on) => {
          onKeep({ pinned: on });
        }}
      />
    </div>
  );
}

/** A part of what's shown about a choice, under a small heading. */
export function ItemSection({
  title,
  aside,
  children,
}: {
  readonly title: string;
  readonly aside?: ReactNode;
  readonly children: ReactNode;
}) {
  return (
    <section className="item-section">
      <div className="item-section-head">
        <h4 className="item-section-title">{title}</h4>
        {aside && <span className="item-section-hint">{aside}</span>}
      </div>
      {children}
    </section>
  );
}

interface TrySampleProps {
  readonly trial: Trial | undefined;
  /** What a try costs, such as "70 credits". */
  readonly cost: string;
  readonly onTry: () => void;
}

/** The sample, and the choice's take on it once tried. */
export function TrySample({ trial, cost, onTry }: TrySampleProps) {
  const { t } = useI18n();
  return (
    <ItemSection title={t.menus.tryIt} aside={t.menus.tryIntro}>
      <div className="item-try">
        <figure className="item-sample">
          <figcaption>{t.menus.before}</figcaption>
          <p>{t.menus.sample}</p>
        </figure>
        <figure className="item-sample is-after" aria-busy={trial?.status === 'running'}>
          <figcaption>{t.menus.after}</figcaption>
          {trial?.status === 'done' ? (
            <p>{trial.text}</p>
          ) : (
            <button
              type="button"
              className="button button--ghost item-button"
              disabled={trial?.status === 'running'}
              onClick={onTry}
            >
              <Icon name="play" />
              {trial?.status === 'running' ? t.menus.trying : t.menus.run(cost)}
            </button>
          )}
          {trial?.status === 'failed' && (
            <p className="dialog-error" role="alert">
              {trial.message}
            </p>
          )}
        </figure>
      </div>
    </ItemSection>
  );
}
